import {
  statusCanSubmit,
  statusCanCancel,
  successOrderStatuses,
  MaturityOrder,
  OrderStatusEnum,
} from '../../deal-series/deal-series.types';
import { hasOrderAmountApplied, isOrderPassesSubmissionAmountRules } from '../../deal-series/deal-series.util';
import { isSoleMaturityGridRow } from '../../deal-series/submit-preview-order.util';
import { isSplitMaturityGridOrder } from '../../deal-series/split-order.util';
import {
  isOrderReconfirmed,
  isOrderEligibleForReconfirm,
} from '../deal-reconfirm.utils';

/** SUBMITTED_AND_DRAFT row marked for cancellation via zero order amount (Review & Submit path). */
export const isZeroCancelIntentOrder = (
  order: MaturityOrder | null | undefined,
): boolean =>
  !!order &&
  order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT &&
  !!order.IS_ZERO_CANCEL_INTENT;

/**
 * Shared validation for submit vs cancel.
 * Cancel: status in statusCanCancel (ACCEPTED, SUBMITTED, SUBMITTED_*_FAILURE) and not reconfirmed.
 * Zero-cancel intent rows cancel via Review & Submit only — excluded from Cancel modal.
 * Submit: status in statusCanSubmit and base submission amount rules (quantity vs maturity,
 * multiples, min/max order amount). Zero-cancel intent bypasses amount rules.
 */
const orderValidForSubmit = (
  order: MaturityOrder | null | undefined,
  isCancellation: boolean,
): boolean => {
  if (!order) return false;
  if (isZeroCancelIntentOrder(order)) {
    return isCancellation ? false : !isOrderReconfirmed(order);
  }
  if (isCancellation) {
    return statusCanCancel.has(order.ORDER_STATUS) && !isOrderReconfirmed(order);
  }
  if (!statusCanSubmit.has(order.ORDER_STATUS)) return false;
  return isOrderPassesSubmissionAmountRules(order);
};

/** — Can submit: status, quantity, multiples, min amount; not reconfirmed. */
export const isOrderValidForSubmission = (order: MaturityOrder | null | undefined): boolean =>
  orderValidForSubmit(order, false);

/**
 * — Can cancel: cancellable status and not reconfirmed.
 * When inReconfirmWindow is true, only orders eligible for reconfirm (affected) are valid for cancellation.
 */
export const isOrderValidForCancellation = (
  order: MaturityOrder | null | undefined,
  inReconfirmWindow?: boolean,
): boolean => {
  if (!orderValidForSubmit(order, true)) return false;
  if (inReconfirmWindow) return isOrderEligibleForReconfirm(order);
  return true;
};

/** — Included in Review & Submit modal list: submittable (draft) or reconfirm-eligible. */
export const isOrderRelevantForSubmitModal = (order: MaturityOrder): boolean =>
  (order.ORDER_STATUS != null && statusCanSubmit.has(order.ORDER_STATUS)) ||
  isOrderEligibleForReconfirm(order);

/**
 * — Reconfirm submit actionability in reprice window:
 * order must be reconfirm-eligible and pass base submission amount rules.
 */
export const isOrderActionableForReconfirmSubmit = (
  order: MaturityOrder | null | undefined,
): boolean => {
  if (!order) return false;
  return isOrderEligibleForReconfirm(order) && isOrderPassesSubmissionAmountRules(order);
};

/** — Show in Review & Submit list: outside reprice window = draft or success; in reprice = actionable reconfirm orders only. */
export const isOrderRelevantForSubmitModalList = (
  order: MaturityOrder,
  inRepriceWindow: boolean,
): boolean => {
  if (order.ORDER_STATUS == null) return false;
  if (!inRepriceWindow) {
    if (isZeroCancelIntentOrder(order)) return true;
    if (successOrderStatuses.has(order.ORDER_STATUS)) return true;
    if (!statusCanSubmit.has(order.ORDER_STATUS)) return false;
    return hasOrderAmountApplied(order);
  }
  return isOrderActionableForReconfirmSubmit(order);
};

/** — Selectable in Review & Submit modal: can actually be submitted. Draft + base rules, or reconfirm-eligible + base rules (base conditions block both submit and selection). */
export const isOrderSelectableInSubmitModal = (order: MaturityOrder | null | undefined): boolean =>
  !!order &&
  (isOrderValidForSubmission(order) ||
    (isOrderEligibleForReconfirm(order) && isOrderPassesSubmissionAmountRules(order)));

/**
 * Grid row visibility for Review & Submit / Cancel modals.
 * DEAL_SUBMIT_ORDERS carries persisted lone + split-child rows; sole maturity placeholders
 * (no ORDER_STATUS yet) are supplemented from ALL_MATURITY_ORDERS and must remain visible.
 */
export const isOrderVisibleInSubmitModalGrid = (
  order: MaturityOrder | null | undefined,
  options: {
    cancellation: boolean;
    inRepriceWindow: boolean;
    cancelOnlyAffectedOrders: boolean;
  },
): boolean => {
  if (!order || isSplitMaturityGridOrder(order)) return false;

  if (options.cancellation) {
    return options.cancelOnlyAffectedOrders
      ? isOrderValidForCancellation(order, true)
      : order.ORDER_STATUS != null && statusCanCancel.has(order.ORDER_STATUS);
  }

  if (options.inRepriceWindow) {
    return isOrderRelevantForSubmitModalList(order, true);
  }

  if (isOrderRelevantForSubmitModalList(order, false)) return true;
  return isSoleMaturityGridRow(order);
};
